import { ToDo } from "../../Components/ToDo";
import { Step } from "../Step/Step";
import { Cash } from "../Step/Cash";
import { StepEvent, StepEventType } from "../Step/StepEvent";
import { StepType } from "../Step/StepType";
import { PaymentReturnInfoForCustomer } from "./Components/PaymentReturnInfoForCustomer";
import { PaymentReturnInfoForAdmin } from "./Components/PaymentReturnInfoForAdmin";
import { PaymentStepData } from "./Models/PaymentStepData";
import { PaymentStepState } from "./Models/PaymentStepState";
import { PaymentForm, PaymentFormNames } from "../../Models/Payment/PaymentForm";
import { Line, MultilineInput, Switch } from "@tblabs/truffle";
import { DateTimeProvider } from "../../Services/DateTimeProvider";
import { CashType } from "../Step/CashType";
import { TimeUtils } from "../../Utils/TimeUtils";
import { appendable } from "@tblabs/truffle/Core/appendable";


export class PaymentReturnStep extends Step<PaymentStepState>
{
    public Type = StepType.PaymentReturn;
    public Icon = "💸";
    public TitleForCustomer = () => `Zwrot środków`;
    public Title = () => `Zwrot środków (${PaymentFormNames[this.Data.Form.value]})`;

    public InitialState = PaymentStepState.Awaiting;
    public WellDoneStates = [PaymentStepState.Paid];
    public FailedDoneStates = [PaymentStepState.Canceled, PaymentStepState.Omitted];

    public get AsEvents(): StepEvent[]
    {
        return [
            new StepEvent(StepEventType.PaymentReturn, this.Order.Id, "💰", "Zwrot środków", this.Data.Deadline.value, this.IsDone)
        ];
    }
    public get Mana(): Cash[]
    {
        const cash: Cash[] = [];

        if (this.Data.State.Is(PaymentStepState.Paid))
        {
            cash.push(new Cash(CashType.ReturnedDeposit, this, this.Data.Amount.value, `Zwrot środków (${this.Data.Category.value})`, new Date(+this.Data.WhenPaid.value || +this.Data.Deadline.value)))
        }

        return cash;
    }
    public get WhenPlanned(): Date
    {
        return this.Data.Deadline.value;
    }
    public get WhenDone(): Date
    {
        return new Date(+this.Data.WhenPaid.value || +this.Data.Deadline.value);
    }

    constructor(public Data: PaymentStepData)
    {
        super(Data);
    }

    public Init(): void
    { }

    private AwaitingPaymentCustomerInfo(): appendable[]
    {
        if (this.Data.Form.Is(PaymentForm.Unset))
        {
            return [`⚠️ Forma zwrotu nie została określona. Skontaktuj się z nami aby naprawić ten błąd.`];
        }

        return [
            `Oczekuj zwrotu środków`,
            new PaymentReturnInfoForCustomer(this.Data, this.Order.People.First()),
            // `Średni czas transferu to 1-2 dni robocze.\nW przypadku większego opóźnienia - skontaktuj się z nami`
        ];
    }

    public STATES = {
        [PaymentStepState.Awaiting]: {
            Timeout: () => this.Data.Deadline,
            Customer: {
                StateName: `Oczekiwanie na zwrot środków`,
                Content: () => this.AwaitingPaymentCustomerInfo(),
            },
            Admin: {
                StateName: `Oczekuje na zwrot (${PaymentFormNames[this.Data.Form.value]}) ${(TimeUtils.DaysAgo(this.Data.Deadline) > 0 ? `od ${TimeUtils.DaysAgo(this.Data.Deadline)} dni` : "")}`,
                Content: () => [
                    `Do zwrotu:\n`,
                    new PaymentReturnInfoForAdmin(this.Data, this.Order.People.First()),
                    new ToDo(`Wykonaj zwrot zależnie od formy planowanej płatności`)
                ],
                Actions: {
                    "✅": () => { this.Data.WhenPaid.value = DateTimeProvider.Now; this.GoTo(PaymentStepState.Paid); },
                    "❌": () => this.GoTo(PaymentStepState.Canceled),
                },
            },
        },
        [PaymentStepState.Paid]: {
            StateName: "Zwrócone",
            Customer: {
                Content: () => [
                    `✅ Zwrot wykonany ${TimeUtils.FormattedDateTime(this.Data.WhenPaid.value)}`,
                    new Line(),
                    new Switch<PaymentForm, string>(this.Data.Form.value)
                        .Case(PaymentForm.Crypto, () => "Czas transferu wynosi od kilku minut do kilku godzin")
                        .Case(PaymentForm.BankTransfer, () => "Czas transferu wynosi zwykle około 24 godzin")
                        .Case(PaymentForm.Blik, () => "Czas transferu to zwykle kilka minut")
                        .Case(PaymentForm.Cash, () => "Zwrot gotówką")
                        .Case(PaymentForm.PostalTransfer, () => "Czas transferu wynosi 1-3 dni robocze")
                        .Default(() => "Forma zwrotu nie została określona. Skontaktuj się z nami aby naprawić ten błąd.")
                        .Execute(),
                ],
            },
            Admin: {
                Content: () => [
                    `✅ Zwrot wykonany`,
                    new PaymentReturnInfoForAdmin(this.Data, this.Order.People.First()),
                ],
            },
        },
        [PaymentStepState.Canceled]: {
            StateName: "Odwołane",
            Customer: {
                Content: () => [`❌ Anulowano zwrot`],
            },
            Admin: {
                Content: () => [
                    `❌ Przelew anulowany (${this.Data.AmountAsString})`,
                    new ToDo(
                        `Jeśli tylko pominięty klinij X`,
                        new MultilineInput(this.Data.CancellationReason).Placeholder("Powód pominięcia zwrotu. Widoczne również dla klienta!").Rows(3)
                    )],
                Actions: {
                    "❌": () => this.GoTo(PaymentStepState.Omitted),
                },
            },
        },
        [PaymentStepState.Omitted]: {
            StateName: "Zwrot środków został pominięty",
            Customer: {
                Content: () => [`✖️ Zwrot został pominięty z powodu: ${this.Data.CancellationReason.value || "nie podano"}.`],
            },
            Admin: {
                Content: () => [
                    `✖️ Zwrot pominięty (${this.Data.AmountAsString})`,
                    new Line(),
                    this.Data.CancellationReason.value
                ],
            },
        },
        [PaymentStepState.Error]: {
            StateName: "Błąd płatności",
            Customer: {
                Content: () => [
                    `Błąd podczas wykonywania operacji.`],
            },
            Admin: {
                Content: () => [
                    `Wystąpił błąd podczas wykonywania operacji. Sytuacja wymaga wyjaśnienia!`],
            },
        },
        [PaymentStepState.Returned]: {
            StateName: "Zwrócone",
            Customer: {
                Content: () => [
                    `Płatność została anulowana przez bank. Trwa wyjaśnianie sytuacji...`],
            },
            Admin: {
                Content: () => [
                    `Płatność została anulowana przez bank. Sytuacja wymaga wyjaśnienia!`,
                ],
            },
        },
        [PaymentStepState.UnableToExecute]: {
            StateName: "Problem z wykonaniem zwrotu środków",
            Customer: {
                Content: () => [
                    `Nie można wykonać zwrotu. Powód: ${this.Data.CancellationReason.value || "nie podano"}.`,
                    new Line(),
                    "Skontaktuj się z nami aby rozwiązać problem."],
            },
            Admin: {
                Content: () => [
                    `Nie można wykonać zwrotu. Powód: ${this.Data.CancellationReason.value || "nie podano"}.`,
                ],
            },
        },
    };
}
